import React from 'react'
import { Button, Col, Divider, message, Result, Skeleton, Typography } from 'antd'
import { ArrowDownOutlined, ArrowUpOutlined } from '@ant-design/icons'
import { FormComponent } from '../../../BaseComponent'
import { TableColumn, VerticalTableTransfer } from '../../../VerticalTableTransfer'
import { ApplicationTagContextData } from '../../../../context/ApplicationTagContext'
import { ShelterContextData } from '../../../../context/ShelterContext'
import IApplicationTag from '../../../../interfaces/IApplicationTag'
import IApplicationTagShelter from '../../../../interfaces/IApplicationTagShelter'
import { ApplicationTagShelterSaveRequest } from '../../../../services/api/interfaces/ApiRequest'

import ProcessTagEvent from './Events'

interface ApplicationTag extends IApplicationTag {
  position: number
}

export type ProcessTagProps = {
  shelterUuid: string,

  context: ShelterContextData,
  applicationTagContext: ApplicationTagContextData
}

export default class ProcessTag extends FormComponent<ProcessTagProps> {

  private tableTransferColumns: TableColumn = {
    source: [
      {
        title: 'Name',
        dataIndex: 'name',
        searchable: true
      },
      {
        title: 'Description',
        dataIndex: 'description'
      }
    ],
    target: [
      {
        dataIndex: 'action',
        title: 'Move',
        render: (text, record: ApplicationTag) => {
          const lastRecord = this.state.shelterTags[this.state.shelterTags.length - 1]
          const canGoUp = record.position > 0
          const canGoDown = lastRecord && record.uuid !== lastRecord.applicationTag.uuid

          return (
            <>
              <Button size="small" type="dashed" shape="circle" icon={<ArrowUpOutlined />} disabled={!canGoUp} style={{marginBottom: 5, marginRight: 5}} onClick={() => this.handlePositionChange(record, 'up')} />
              <Button size="small" type="dashed" shape="circle" icon={<ArrowDownOutlined />} disabled={!canGoDown} onClick={() => this.handlePositionChange(record, 'down')} />
            </>
          )
        }
      },
      {
        title: 'Name',
        dataIndex: 'name',
      },
      {
        title: 'Description',
        dataIndex: 'description'
      }
    ]
  }

  state = {
    loaded: false,

    shelterTags: [] as IApplicationTagShelter[]
  }

  async componentDidMount() {
    const shelterTags: IApplicationTagShelter[] = await this.load()

    this.setState({
      loaded: true,
      shelterTags: shelterTags.map(v => {
        return {
          applicationTag: v.applicationTag,
          position: Number(v.position)
        }
      })
    })
  }

  private async load() {
    const { shelterUuid, applicationTagContext } = this.props

    const options = {
      _force_ids: true,
      limit:9999
    }

    await this.loadShelterData()

    await applicationTagContext.requestApplicationTagList({
      options
    })

    await applicationTagContext.requestShelterEventList({
      organization_shelter_uuid: shelterUuid
    })

    const shelterTags = await applicationTagContext.requestShelterTagList({
      organization_shelter_uuid: shelterUuid
    })

    return shelterTags
  }

  loadShelterData = async () => {
    const { shelterUuid, context } = this.props

    if (shelterUuid) {
      await context.requestShelterFind({
        search: {
          uuid: shelterUuid,
          contract_options: {
            with: {
              deleted: false
            }
          },
          organization_question_shelters: {
            with: {
              deleted: false,
              kiosk_reason_id: null,
              question_shelter_species: {
                with: true
              }
            }
          },
          organization_branches: {
            with: {
              deleted: false
            }
          },
          shelter_contract: {
            with: true
          },
          country: {
            with: {
              default_language: {
                with: true
              }
            }
          }
        },
        options: {
          _force_ids: true
        }
      })
    }
  }

  private handleTableTransferChange = (selectedKey: any[]) => {
    const { applicationTagContext } = this.props
    const { shelterTags } = this.state

    const changed = shelterTags.filter(v => selectedKey.includes(v.applicationTag.uuid))
    selectedKey.forEach(uuid => {
      const exists = !!shelterTags.find(v => v.applicationTag.uuid === uuid)
      const applicationTag = applicationTagContext.applicationTags?.find(v => v.uuid === uuid)

      if (!exists && applicationTag) {
        changed.push({
          applicationTag,
          position: changed.length
        })
      }
    })

    this.setState({
      shelterTags: changed
    })
  }

  private handlePositionChange = (record: ApplicationTag, direction: 'up' | 'down') => {
    const { shelterTags } = this.state

    let selected: IApplicationTagShelter[] = Object.assign([], shelterTags)

    const index = selected.findIndex(v => v.applicationTag.uuid === record.uuid)
    let moved = false
    if (index >= 0) {
      if (direction === 'up' && record.position > 0) {
        selected[index].position = record.position - 1
        moved = true
      }
      if (direction === 'down') {
        selected[index].position = record.position + 1
        moved = true
      }
    }


    // Update others position
    if (moved) {
      for (let shelterTag of selected) {
        if (shelterTag.applicationTag.uuid !== record.uuid && shelterTag.position === selected[index].position) {
          if (direction === 'up') {
            // Increase one position if there's any shelterTag in the new record position
            shelterTag.position = shelterTag.position + 1
          } else {
            // Decrease one position if there's any shelterTag in the new record position
            shelterTag.position = shelterTag.position - 1
          }
        }
      }
    }

    setTimeout(() => {
      this.setState({
        shelterTags: selected.sort((a, b) => a.position - b.position)
      })
    }, 100)
  }


  private handleActiveProcessTag = async () => {
    const { shelterUuid, applicationTagContext } = this.props
    
    try {
      await applicationTagContext.requestApplicationTagEnable({
        organization_shelter_uuid: shelterUuid
      })

      await this.loadShelterData()
      
      message.success('Process tag feature activated!')
    } catch (error) {
      message.error(`Failed to activate process tag feature. Reason: ${error}`)
    }
  }

  private handleSaveTags = async () => {
    const { shelterUuid, applicationTagContext } = this.props
    const { shelterTags } = this.state
    


    
    try {
      let TempTags = shelterTags.sort((a, b) => (a.position > b.position) ? 1 : -1);
      for(let i = 0; i< TempTags.length; i++){
        TempTags[i].position = i;
      }

      const payload: ApplicationTagShelterSaveRequest = {
        organization_shelter_uuid: shelterUuid,
        organization_shelter_tags: shelterTags.map(v => {
          return {
            uuid: v.applicationTag.uuid,
            position: v.position
          }
        })
      }

      await applicationTagContext.requestShelterTagSave(payload)
      message.success('Shelter application tags saved successfully!')
    } catch (error) {
      message.error(`Could not save shelter application tags. Reason: ${error}`)
    }
  }

  render() {
    const { shelterUuid, context, applicationTagContext } = this.props
    const { loaded, shelterTags } = this.state

    if (!loaded) {
      return <Skeleton />
    }

    if (!context.shelter?.application_tag) {
      return (
        <Result
          status="404"
          title="Processs Tag not activated"
          extra={<Button type="primary" loading={applicationTagContext.fetching || false} onClick={this.handleActiveProcessTag}>Activate Process Tag Feature</Button>}
        />
      )
    }

    const dataSource = applicationTagContext.applicationTags?.map(v => {
      const shelterTag = shelterTags.find(x => x.applicationTag.uuid === v.uuid)

      return {
        ...v,
        position: shelterTag?.position || 0
      }
    }) || []

    return (
      <Col>
          <Typography.Title level={5}>Shelter Process Tags</Typography.Title>
          <Typography.Text>Here you can define which process tags will be available for the shelter, as well as their order of priority. When configuring these, your first step should be to select from the “source” table which process tags you’d like to add to the shelter. When they have all been selected, click on the ADD button, and they will move to the “target” table. After adding all the desired process tags to the shelter, it is time to prioritize them from top to bottom. The process tags at the top will have lesser priority, while the ones placed at the bottom of the table will have greater priority. Process Tags with greater priority should be the ones closest to the completion of an adoption, like “Home trial.”</Typography.Text>
          <VerticalTableTransfer
            rowKey="uuid"
            columns={this.tableTransferColumns}
            dataSource={dataSource.sort((a, b) => a.position - b.position)}
            intialSelectedKeys={shelterTags.map(v => v.applicationTag.uuid)}
            onChange={this.handleTableTransferChange}
          />

          <Divider />
      
          

          <ProcessTagEvent
            shelterUuid={shelterUuid}
            applicationTagContext={applicationTagContext}
          />
          <Divider />
          <Button
            type="primary"
            size="large"
            loading={!!applicationTagContext.fetching}
            onClick={() => this.handleSaveTags()}
            block
          >
            Save Process Tags
          </Button>
      </Col>
    )
  }
}
